import Info from 'components/form/Info';
import Input from 'components/form/Inputs/Input';
import MENTEE_EXPECTATIONS, { menteeExpectations } from 'enums/MenteeExpectations';
import Success from 'components/form/Success';
import Warning from 'components/form/Warning';
import EXPERTISE, { expertises, ExpertiseValues } from 'enums/Expertise';
import FORMATIONS, { formations } from 'enums/Formation';
import FORMATION_PROJECT_MATCHES from 'enums/FormationProjectMatches';
import FORMATION_STATES from 'enums/FormationState';
import HOBBIES, { hobbies } from 'enums/Hobby';
import MENTEE_HEARD_ABOUT_MENTORSHIP, {
  menteeHeardAboutMentorships,
  MenteeHeardAboutMentorshipValues,
} from 'enums/MenteeHeardAboutMentorship';
import { MENTEE_PRIORITY_LEVELS } from 'enums/PriorityLevels';
import STUDY_LEVELS, { studyLevels } from 'enums/StudyLevels';
import VOLUNTEER_STATES from 'enums/VolunteerState';
import { FC } from 'react';
import Button from '../../Button';
import Error from '../../form/Error';
import { FormRadio, FormRadioGroup } from '../../form/FormRadioGroup';
import { FlexColumn, Grid } from '../../form/generic';
import LabelledCheckbox from '../../form/Inputs/LabelledCheckbox';
import Radio, { getRadioValue } from '../../form/Inputs/Radio';
import Select from '../../form/Inputs/Select';
import TitleList from '../../form/TitleList';
import Field from '../../profileNavigation/Field';
import Head from '../../profileNavigation/Head';
import {
  ColumnRadioContainer,
  Form,
  HeadContainer,
  PermissionsContainer,
  RadiosContainer,
  Subtitle,
} from './components';
import { Errors, initialErrors, State } from './types';

type Props = {
  title: string;
  subtitle: string | string[];
  state: State;
  loading: boolean;
  setState: React.Dispatch<React.SetStateAction<State>>;
  handleSubmit: React.FormEventHandler;
  errors: Errors;
  setErrors: React.Dispatch<React.SetStateAction<Errors>>;
  marginTop?: number | string;
  success?: boolean;
  invalid?: boolean;
};
const MenteeFormEdit: FC<Props> = ({
  title,
  subtitle,
  state,
  loading,
  setState,
  handleSubmit,
  errors,
  setErrors,
  marginTop,
  success,
  invalid,
}) => {
  const handleChange: React.ChangeEventHandler<
    HTMLInputElement & HTMLSelectElement & HTMLTextAreaElement
  > = (e) => {
    let set;
    switch (e.target.name) {
      case 'jobSearch':
      case 'careerPlan':
      case 'studies':
      case 'entrepreneurship':
        setErrors(initialErrors);
        return setState({
          ...state,
          [e.target.name]: getRadioValue(e.target.value),
          sharing: false,
        });
      case 'volunteerState':
      case 'validatedDiploma':
      case 'completedFormation':
      case 'formationMatchingProject':
      case 'intendedFormation':
      case 'formationState':
      case 'intendedDiploma':
      case 'hobby':
        setErrors(initialErrors);
        return setState({
          ...state,
          [e.target.name]: getRadioValue(e.target.value),
        });

      case 'sharing':
        if (e.target.checked) {
          return setState({
            ...state,
            jobSearch: null,
            careerPlan: null,
            studies: null,
            entrepreneurship: null,
            sharing: true,
          });
        }
        return setState({
          ...state,
          sharing: false,
        });

      case 'expertiseSelect':
        if (e.target.value !== 'DOMAIN') {
          set = new Set(state.expertises);
          set.add(e.target.value as ExpertiseValues);
          if (Array.from(set).length < 3) {
            return setState({
              ...state,
              expertises: Array.from(set),
            });
          }
          return setErrors({
            ...initialErrors,
            expertises: true,
          });
        }
        return;
      case 'expertiseCheck':
        setErrors(initialErrors);
        set = new Set(state.expertises);
        set.delete(e.target.value as ExpertiseValues);
        return setState({
          ...state,
          expertises: Array.from(set),
        });

      case 'permissions':
        setErrors(initialErrors);
        return setState({
          ...state,
          [e.target.value]: e.target.checked,
        });

      case 'heardAboutMentorship':
        return setState({
          ...state,
          [e.target.name]:
            (e.target.value as MenteeHeardAboutMentorshipValues) || null,
        });

      default:
        return setState({
          ...state,
          [e.target.name]: e.target.value,
        });
    }
  };

  return (
    <section style={{ maxWidth: '100%', marginTop }}>
      <HeadContainer>
        <Head title={title} subtitle={subtitle} />
        {(errors.priorityLevels || errors.badRequest) && (
          <Error>
            Le formulaire est invalide. Veuillez vérifier vos réponses.
          </Error>
        )}
        {success && <Success>Votre questionnaire a été enregistré.</Success>}
        {errors.general && (
          <Error>
            Une erreur s'est produite. Veuillez réessayer ultérieurement.
          </Error>
        )}
        {invalid && (
          <Warning>
            Le formulaire enregistré n'est pas valide. Vous devez enregistrer un
            formulaire valide pour passer à l'étape suivante.
          </Warning>
        )}
      </HeadContainer>
      <Form onSubmit={handleSubmit}>
        <Field label="Votre volontariat ou bénévolat *">
          <ColumnRadioContainer>
            <Radio
              label={VOLUNTEER_STATES['ONGOING']}
              checked={state.volunteerState === 'ONGOING'}
              onChange={handleChange}
              name="volunteerState"
              id="ONGOING"
            />
            <Radio
              label={VOLUNTEER_STATES['FINISHED']}
              checked={state.volunteerState === 'FINISHED'}
              onChange={handleChange}
              name="volunteerState"
              id="FINISHED"
            />
            <Radio
              label={VOLUNTEER_STATES['OTHER']}
              checked={state.volunteerState === 'OTHER'}
              onChange={handleChange}
              name="volunteerState"
              id="OTHER"
            />
          </ColumnRadioContainer>
        </Field>
        <Field
          label={
            <>
              Vous souhaitez un appui pour *
              <i style={{ fontSize: '.9rem', color: 'var(--gray-500)' }}>
                (vous ne pouvez cocher que 2 "Prioritaire" au maximum)
              </i>
            </>
          }
        >
          <FlexColumn>
            {errors.priorityLevels && (
              <Error style={{ marginBottom: '1rem' }}>
                Vous ne pouvez choisir que 2 priorités.
              </Error>
            )}
            <FormRadioGroup
              title="L'amélioration des techniques de recherche d'emploi"
              name="jobSearch"
              value={state.jobSearch}
            >
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['PRIORITY']}
                id="PRIORITY"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['YES']}
                id="YES"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['NO']}
                id="NO"
                onChange={handleChange}
              />
            </FormRadioGroup>
            <FormRadioGroup
              title="Définition du projet de formation ou projet professionnel"
              name="careerPlan"
              value={state.careerPlan}
            >
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['PRIORITY']}
                id="PRIORITY"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['YES']}
                id="YES"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['NO']}
                id="NO"
                onChange={handleChange}
              />
            </FormRadioGroup>
            <FormRadioGroup
              title="Conseils pour réussir dans ses études"
              name="studies"
              value={state.studies}
            >
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['PRIORITY']}
                id="PRIORITY"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['YES']}
                id="YES"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['NO']}
                id="NO"
                onChange={handleChange}
              />
            </FormRadioGroup>
            <FormRadioGroup
              title="Création ou développement d'une activité"
              name="entrepreneurship"
              value={state.entrepreneurship}
            >
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['PRIORITY']}
                id="PRIORITY"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['YES']}
                id="YES"
                onChange={handleChange}
              />
              <FormRadio
                label={MENTEE_PRIORITY_LEVELS['NO']}
                id="NO"
                onChange={handleChange}
              />
            </FormRadioGroup>
            <TitleList title="Partage">
              <LabelledCheckbox
                label="Je souhaite pouvoir partager avec un mentor de façon générale"
                onChange={handleChange}
                checked={state.sharing}
                name="sharing"
              />
            </TitleList>
          </FlexColumn>
        </Field>

        <Field
          label={
            <>
              Dans quel domaine de compétences souhaitez-vous construire votre
              projet ? *<Subtitle>2 domaines maximum</Subtitle>
            </>
          }
        >
          <ColumnRadioContainer>
            {errors.expertises && (
              <Error>
                Vous ne pouvez sélectionner que 2 domaines maximum. Si vous
                souhaitez changer un domaine, veuillez le décocher avant de
                choisir un autre domaine.
              </Error>
            )}
            <Select
              value="DOMAIN"
              name="expertiseSelect"
              onChange={handleChange}
            >
              <option value="DOMAIN">-- Sélectionnez un domaine --</option>
              {expertises.map((expertise, idx) => (
                <option key={idx} value={expertise}>
                  {EXPERTISE[expertise]}
                </option>
              ))}
            </Select>
            <RadiosContainer>
              {state.expertises.map((expertise, idx) => (
                <LabelledCheckbox
                  key={idx}
                  label={EXPERTISE[expertise]}
                  checked
                  onChange={handleChange}
                  name="expertiseCheck"
                  value={expertise}
                />
              ))}
            </RadiosContainer>
          </ColumnRadioContainer>
        </Field>

        <Field
          label={
            <>
              Quel niveau de formation le plus élevé avez-vous validé à ce jour
              ? *
              <Subtitle>
                y-compris dans un autre domaine que celui de votre projet
              </Subtitle>
            </>
          }
        >
          <Grid>
            {studyLevels.map((studyLevel, idx) => (
              <Radio
                key={idx}
                label={STUDY_LEVELS[studyLevel]}
                checked={state.validatedDiploma === studyLevel}
                onChange={handleChange}
                name="validatedDiploma"
                id={studyLevel}
              />
            ))}
          </Grid>
        </Field>
        <Field label="Dans quel type de formation ? *">
          <Grid>
            {formations.map((formation, idx) => (
              <Radio
                key={idx}
                label={FORMATIONS[formation]}
                checked={state.completedFormation === formation}
                onChange={handleChange}
                name="completedFormation"
                id={formation}
              />
            ))}
          </Grid>
        </Field>

        <Field label="La formation dans laquelle vous avez validé le niveau de formation le plus élevé correspond-elle à votre projet actuel ? *">
          <RadiosContainer>
            <Radio
              label={FORMATION_PROJECT_MATCHES['YES']}
              checked={state.formationMatchingProject === 'YES'}
              onChange={handleChange}
              name="formationMatchingProject"
              id="YES"
            />
            <Radio
              label={FORMATION_PROJECT_MATCHES['NO']}
              checked={state.formationMatchingProject === 'NO'}
              onChange={handleChange}
              name="formationMatchingProject"
              id="NO"
            />
            <Radio
              label={FORMATION_PROJECT_MATCHES['NOT_CONCERNED']}
              checked={state.formationMatchingProject === 'NOT_CONCERNED'}
              onChange={handleChange}
              name="formationMatchingProject"
              id="NOT_CONCERNED"
            />
          </RadiosContainer>
        </Field>

        {state.formationMatchingProject === 'NO' && (
          <Field label="Quel type de formation correspond à votre projet actuel ?">
            <Grid>
              {formations.map((formation, idx) => (
                <Radio
                  key={idx}
                  label={FORMATIONS[formation]}
                  checked={state.intendedFormation === formation}
                  onChange={handleChange}
                  name="intendedFormation"
                  id={formation}
                />
              ))}
            </Grid>
          </Field>
        )}

        <Field label="Où en êtes-vous dans la formation qui correspond à votre projet actuel ? *">
          <ColumnRadioContainer>
            <Radio
              label={FORMATION_STATES['FINISHED']}
              checked={state.formationState === 'FINISHED'}
              onChange={handleChange}
              name="formationState"
              id="FINISHED"
            />
            <Radio
              label={FORMATION_STATES['ONGOING']}
              checked={state.formationState === 'ONGOING'}
              onChange={handleChange}
              name="formationState"
              id="ONGOING"
            />
            <Radio
              label={FORMATION_STATES['NOT_STARTED']}
              checked={state.formationState === 'NOT_STARTED'}
              onChange={handleChange}
              name="formationState"
              id="NOT_STARTED"
            />
          </ColumnRadioContainer>
        </Field>

        {state.formationState !== 'FINISHED' && (
          <Field label="Quel niveau de diplôme visez-vous dans la formation correspondant à votre projet ?">
            <Grid>
              {studyLevels.map((studyLevel, idx) => (
                <Radio
                  key={idx}
                  label={STUDY_LEVELS[studyLevel]}
                  checked={state.intendedDiploma === studyLevel}
                  onChange={handleChange}
                  name="intendedDiploma"
                  id={studyLevel}
                />
              ))}
            </Grid>
          </Field>
        )}

        <Field label="Lorsque vous avez du temps libre, si vous avez le choix, vous choisissez plutôt *">
          <Grid>
            {hobbies.map((hobby, idx) => (
              <Radio
                key={idx}
                label={HOBBIES[hobby]}
                checked={state.hobby === hobby}
                onChange={handleChange}
                name="hobby"
                id={hobby}
              />
            ))}
          </Grid>
        </Field>

        <Field label="Comment avez-vous entendu parler du mentorat?">
          <FlexColumn gap={0.5}>
            <Select
              value={state.heardAboutMentorship || ''}
              onChange={handleChange}
              name="heardAboutMentorship"
            >
              <option value="">-- Sélectionner une option --</option>
              {menteeHeardAboutMentorships.map((value, idx) => (
                <option key={idx} value={value}>
                  {MENTEE_HEARD_ABOUT_MENTORSHIP[value]}
                </option>
              ))}
            </Select>
            {state.heardAboutMentorship === 'OTHER' && (
              <Input
                value={state.heardAboutMentorshipText || ''}
                onChange={handleChange}
                name="heardAboutMentorshipText"
                placeholder="Veuillez préciser ici"
              />
            )}
          </FlexColumn>
        </Field>

        <Field label="Ce que j'attends du mentorat *">
          <FlexColumn gap={0.5}>
            <Info>Ce champ pourra être vu par votre mentor.</Info>
            {errors.expectations && (
              <Error>Veuillez sélectionner au moins une option.</Error>
            )}
            {menteeExpectations.map((expectation) => {
              const selected = (state.expectations || '').split(',').filter(Boolean);
              return (
                <LabelledCheckbox
                  key={expectation}
                  label={MENTEE_EXPECTATIONS[expectation]}
                  checked={selected.includes(expectation)}
                  onChange={(e) => {
                    const set = new Set(selected);
                    if (e.target.checked) {
                      set.add(expectation);
                    } else {
                      set.delete(expectation);
                    }
                    setErrors(initialErrors);
                    setState({
                      ...state,
                      expectations: Array.from(set).join(','),
                    });
                  }}
                />
              );
            })}
          </FlexColumn>
        </Field>

        <Field label="Partager mes informations avec le mentor">
          <PermissionsContainer>
            {errors.permissions && <Error>Veuillez choisir une option.</Error>}
            <LabelledCheckbox
              label="J’autorise le partage de mon adresse email avec le mentor"
              checked={!!state.sharingEmail}
              name="permissions"
              value="sharingEmail"
              onChange={handleChange}
            />
            <LabelledCheckbox
              label="J’autorise le partage de mon numéro de téléphone avec le mentor"
              checked={!!state.sharingPhone}
              name="permissions"
              value="sharingPhone"
              onChange={handleChange}
            />
          </PermissionsContainer>
        </Field>

        <Button color="primary" loading={loading}>
          Valider le questionnaire
        </Button>
      </Form>
    </section>
  );
};

export default MenteeFormEdit;
